import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, useIsAdmin } from '@/features/auth/AuthContext';
import { changePassword } from '@/features/auth/api';
import { ApiError } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { inputClass } from '@/components/ui/Field';
import { UsersManager } from './profile/UsersManager';
import { DepartmentCard } from './profile/DepartmentCard';

function initialsOf(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .map((p) => p[0])
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?'
  );
}

export function ProfilePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, setUser, logout } = useAuth();
  const isAdmin = useIsAdmin();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  if (!user) return null;

  async function handleChangePassword() {
    setError(null);
    setOk(null);
    if (next.trim().length < 6) {
      setError(t('profile.passwordTooShort'));
      return;
    }
    setSaving(true);
    try {
      await changePassword(current, next);
      setCurrent('');
      setNext('');
      setOk(t('profile.passwordChanged'));
      if (user!.mustChangePassword) setUser({ ...user!, mustChangePassword: false });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.generic'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4">
      <Card className="flex flex-col items-center gap-2 p-5">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-conduril text-[22px] font-bold text-white">
          {initialsOf(user.displayName)}
        </div>
        <div className="text-lg font-bold">{user.displayName}</div>
        <div className="text-[13px] text-conduril-muted">
          {user.username} ·{' '}
          <span className="rounded-full bg-conduril-tint px-2.5 py-0.5 font-bold text-conduril">
            {t(`roles.${user.role}`)}
          </span>
        </div>
      </Card>

      {user.mustChangePassword && (
        <div className="rounded-xl border border-[#e0d090] bg-[#fff8dc] px-3.5 py-3 text-sm">
          {t('profile.mustChange')}
        </div>
      )}

      <Card className="flex flex-col gap-2.5 p-4">
        <h2 className="text-base font-bold">{t('profile.changePassword')}</h2>
        <input
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          placeholder={t('profile.currentPassword')}
          autoComplete="current-password"
          className={inputClass}
        />
        <input
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          placeholder={t('profile.newPassword')}
          autoComplete="new-password"
          className={inputClass}
        />
        <ErrorBanner message={error} />
        {ok && (
          <div className="rounded-xl border border-green-300 bg-green-50 px-3.5 py-2.5 text-sm text-green-800">
            {ok}
          </div>
        )}
        <button
          type="button"
          onClick={handleChangePassword}
          disabled={saving}
          className="min-h-[44px] rounded-lg bg-conduril-tint py-2.5 text-sm font-semibold hover:brightness-95 disabled:opacity-60"
        >
          {t('profile.savePassword')}
        </button>
      </Card>

      {isAdmin && <DepartmentCard />}
      {isAdmin && <UsersManager />}

      <div className="flex gap-2.5">
        <Button variant="secondary" onClick={() => navigate('/')} className="flex-1">
          ← {t('common.back')}
        </Button>
        <Button
          variant="danger"
          onClick={async () => {
            await logout();
            navigate('/login', { replace: true });
          }}
          className="flex-1"
        >
          {t('profile.logout')}
        </Button>
      </div>
    </div>
  );
}
